import { t } from 'i18next';
import * as yup from 'yup';

import { COUNTRIES_WITH_STATES, DEFAULT_REQUIRED_FIELDS, postalCodeMap } from 'addresses/constants';
import { AddressFields } from 'addresses/enums';
import { testRequiredSchema } from 'core/schemas';

export const countrySchema = (required = true) =>
  yup
    .object()
    .label(t('labelCountry'))
    .shape({
      name: yup.string().nullable(),
      abbreviation: yup.string().nullable(),
      id: yup.number().nullable(),
    })
    .nullable()
    .test(...testRequiredSchema(required, t('labelCountry')));

export const stateSchema = (isStateObject = false, required = false) =>
  isStateObject
    ? yup
        .object()
        .label(t('labelState'))
        .shape({
          name: yup.string(),
          abbreviation: yup.string(),
          id: yup.number(),
        })
        .nullable()
        .test(...testRequiredSchema(required, t('labelState')))
    : yup
        .string()
        .trim()
        .label(t('labelState'))
        .nullable()
        .test(...testRequiredSchema(required, t('labelState')));

export const addressSchema = (
  extendedShape = true,
  requiredFields: AddressFields[] = DEFAULT_REQUIRED_FIELDS
) => {
  const mainAddressSchema = yup
    .object()
    .shape({
      country: countrySchema(requiredFields.includes(AddressFields.COUNTRY)),
      state: yup.mixed().when('country', (country) => {
        const isStateObject = COUNTRIES_WITH_STATES.includes(country?.abbreviation);

        return isStateObject
          ? stateSchema(true, requiredFields.includes(AddressFields.STATE))
          : stateSchema(false, requiredFields.includes(AddressFields.STATE));
      }),
      zip: yup
        .string()
        .trim()
        .label(t('labelPostalCode'))
        .nullable()
        .test(
          ...testRequiredSchema(requiredFields.includes(AddressFields.ZIP), t('labelPostalCode'))
        )
        .when('country', (country, schema) =>
          COUNTRIES_WITH_STATES.includes(country.abbreviation)
            ? schema.matches(postalCodeMap[country.abbreviation], t('errorInvalidPostalCodeFormat'))
            : schema
        ),
      city: yup
        .string()
        .trim()
        .label(t('labelCity'))
        .nullable()
        .test(...testRequiredSchema(requiredFields.includes(AddressFields.CITY), t('labelCity'))),
    })
    .nullable()
    .required();

  if (!extendedShape) {
    return mainAddressSchema;
  }

  return mainAddressSchema.shape({
    line1: yup
      .string()
      .trim()
      .label(t('labelAddressLine1'))
      .nullable()
      .test(
        ...testRequiredSchema(requiredFields.includes(AddressFields.LINE1), t('labelAddressLine1'))
      ),
    line2: yup
      .string()
      .trim()
      .label(t('labelAddressLine2'))
      .nullable()
      .test(
        ...testRequiredSchema(requiredFields.includes(AddressFields.LINE2), t('labelAddressLine2'))
      ),
    name: yup
      .string()
      .trim()
      .label(t('labelLocationName'))
      .nullable()
      .test(
        ...testRequiredSchema(requiredFields.includes(AddressFields.NAME), t('labelLocationName'))
      ),
  });
};
